<template>
    <div class="mb-6">
        <page-headline>
            {{ $t('pages.settings.title') }}
        </page-headline>
        <div class="flex mt-5">
            <h2 class="section-title">{{ $t('pages.settings.auction_timings.title') }}</h2>
        </div>
        <div class="mt-4 flex items-center">
            <div class="font-bold mr-5">{{ $t('pages.settings.auction_timings.timezone.title') }}</div>
            <div class="ml-5 relative" ref="years">
                <select v-model="selectedTimezone" class="w-full form-select">
                    <template v-for="(b, i) in timeZones">
                        <option :value="i">{{ i }} - {{ b }} {{ auctionTimeZoneDescription(i) }}</option>
                    </template>
                </select>
                <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <icon class="w-4 h-4 inline-block" name="arrow-select"/>
                </div>
            </div>
        </div>
        <div class="mt-5 flex">
            <div class="w-1/2">
                {{ $t('pages.settings.auction_timings.auction_start') }}{{ auctionStartTime | formatTime($t('locale.formatTimeTwelveHours'), selectedTimeZoneName, selectedTimezone) }}
            </div>
            <div class="w-1/2">
                {{ $t('pages.settings.auction_timings.auction_end') }}{{ auctionEndTime | formatTime($t('locale.formatTimeTwelveHours'), selectedTimeZoneName, selectedTimezone) }}
            </div>
        </div>

        <div class="flex mt-8" v-if="showTimesTodayDifferFromDefault">
            <h2 class="section-title">{{ $t('pages.settings.today') }}</h2>
            <icon name="alert-warning" class="w-8 h-8 ml-5 font-bold alert-icon-warning"/>
        </div>
        <auction-settings
            v-if="showTimesTodayDifferFromDefault"
            :auction-time-zone="selectedTimezone"
            :show-times="showTimesToday"
            :time-zone="selectedTimeZoneName"
        />
        <div class="mt-5 flex justify-end" v-if="showTimesTodayDifferFromDefault">
            <a :href="route('controlpanel',  { })" v-if="showTimesToday.length > 0">
                <button type="button" class="btn btn-primary">
                    {{ $t('pages.auctions.planning.overview.control_panel_button_label') }}
                </button>
            </a>
        </div>

        <div class="flex mt-8">
            <h2 class="section-title">{{ $t('pages.settings.default') }}</h2>
        </div>
        <auction-settings
            :auction-time-zone="selectedTimezone"
            :show-times="showTimes"
            :time-zone="selectedTimeZoneName"
        />
        <div class="mt-5 flex justify-end">
            <button @click="editDefaultSettings()" type="button" class="btn btn-primary">
                {{ $t('shared.edit') }}
            </button>
        </div>

        <div class="mt-8 text-md">
            {{ $t('pages.settings.auction_timings.timings.remark_1') }}
        </div>

        <modal
            ref="editDefaultSettingsDialog"
            @confirm="saveSettings"
            @cancel="cancelSettingsEditing"
            :modal-size="'l'"
            :label-confirm="$t('pages.settings.edit_dialog.confirm')"
            :dangerous="false"
            :title="this.$t('pages.settings.edit_dialog.title')"
        >
            <form ref="editSettingsForm">
                <table class="table-default mt-5 mb-3">
                    <thead>
                        <tr>
                            <th scope="col">{{ $t('pages.settings.edit_dialog.columns.move') }}</th>
                            <th scope="col">{{ $t('pages.settings.edit_dialog.columns.phase') }}</th>
                            <th scope="col">{{ $t('pages.settings.edit_dialog.columns.show') }}</th>
                            <th scope="col">{{ $t('pages.settings.edit_dialog.columns.show_start') }}</th>
                            <th scope="col">{{ $t('pages.settings.edit_dialog.columns.price_drop') }}</th>
                            <th scope="col">{{ $t('pages.settings.edit_dialog.columns.show_end') }}</th>
                            <th scope="col">{{ $t('pages.settings.edit_dialog.columns.delete') }}</th>
                        </tr>
                    </thead>
                    <draggable v-model="form" tag="tbody" class="divide-y divide-gray-200 bg-white" ref="settingsRows">
                        <tr v-for="(showTime, index) in form" :key="index" class="hover:bg-gray-100 hover:cursor-move">
                            <td><icon name="draggable" class="w-6 h6 text-gray-300"/></td>
                            <td>{{ index + 1 }}</td>
                            <td>
                                <input
                                    :id="'priceDrop_' + index"
                                    :name="'priceDrop_' + index"
                                    v-model="form[index].show"
                                    @change="updateTimeRanges(index)"
                                    class="mr-1"
                                    type="checkbox">
                            </td>
                            <td>
                                <vue-timepicker
                                    :ref="'showStartTime_' + index"
                                    v-if="form[index].show"
                                    v-model="form[index].showStartTime"
                                    :format="$t('locale.formatTimeTwelveHours')"
                                    @input="updateTimeRanges(index)"
                                    @open="updateTimeRanges(index)"
                                    @focus="updateTimeRanges(index)"
                                    @change="updateTimeRanges(index)"
                                    @close="updateTimeRanges(index)"
                                    @blur="updateTimeRanges(index)"
                                    :hour-range="hourRanges[index]['showStartTime']"
                                    :minute-range="minuteRanges[index]['showStartTime']"
                                    fixed-dropdown-button
                                    hide-clear-button
                                    auto-scroll
                                    advanced-keyboard
                                    manual-input
                                />
                            </td>
                            <td>
                                <vue-timepicker
                                    :ref="'priceDropTime_' + index"
                                    v-model="form[index].priceDropTime"
                                    :format="$t('locale.formatTimeTwelveHours')"
                                    @input="updateTimeRanges(index)"
                                    @open="updateTimeRanges(index)"
                                    @focus="updateTimeRanges(index)"
                                    @change="updateTimeRanges(index)"
                                    @close="updateTimeRanges(index)"
                                    @blur="updateTimeRanges(index)"
                                    :hour-range="hourRanges[index]['priceDropTime']"
                                    :minute-range="minuteRanges[index]['priceDropTime']"
                                    fixed-dropdown-button
                                    hide-clear-button
                                    auto-scroll
                                    advanced-keyboard
                                    manual-input
                                />
                            </td>
                            <td>
                                <vue-timepicker
                                    :ref="'showEndTime_' + index"
                                    v-if="form[index].show"
                                    v-model="form[index].showEndTime"
                                    :format="$t('locale.formatTimeTwelveHours')"
                                    @input="updateTimeRanges(index)"
                                    @open="updateTimeRanges(index)"
                                    @focus="updateTimeRanges(index)"
                                    @change="updateTimeRanges(index)"
                                    @close="updateTimeRanges(index)"
                                    @blur="updateTimeRanges(index)"
                                    :hour-range="hourRanges[index]['showEndTime']"
                                    :minute-range="minuteRanges[index]['showEndTime']"
                                    fixed-dropdown-button
                                    hide-clear-button
                                    auto-scroll
                                    advanced-keyboard
                                    manual-input
                                />
                            </td>
                            <td class="flex items-center">
                                <button type="button" class="btn-danger px-2 py-1" @click="deleteItem(index)">
                                    <icon name="x" class="w-6 h6"/>
                                </button>
                            </td>
                        </tr>
                    </draggable>
                </table>
            </form>

            <div class="mb-5">
                <button type="button" class="btn btn-primary" @click="addAtEnd()">
                    {{ $t('pages.settings.edit_dialog.add_at_end') }}
                </button>
            </div>

            <div v-if="settingsSaved && !this.formIsModified" class="w-full mb-5">
                <div class="bg-white tile h-full">
                    <div class="px-4 py-5 sm:p-6 h-full">
                        <div class="flex items-stretch flex-col h-full">
                            <div class="self-stretch mb-5">
                                <div class="mt-2 max-w-xl text-sm text-gray-500">
                                    {{ $t('pages.settings.edit_dialog.success') }}
                                </div>
                            </div>
                            <div class="mr-auto mt-auto">
                                <a :href="route('auctions.planning', {})">
                                    <button type="button" class="btn btn-primary">
                                        {{$t('pages.dashboard.components.tta.goto_planning_button_label')}}
                                    </button>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="settingsSaved" class="mb-5"></div>
            <alert-message
                v-if="this.showTimes.length !== this.form.length"
                :error-type="ERROR_TYPE_WARNING"
                :show-as-list="false"
                :display-errors-title="false"
                class="mb-5"
            >
                <i18n path="pages.settings.edit_dialog.add_delete_warning" tag="span">
                    <a
                        class="underline"
                        :href="route('auctions.planning.download.currentData', {})"
                    >{{ $t('pages.settings.edit_dialog.add_delete_warning_link') }}</a>
                </i18n>
            </alert-message>

            <alert-message
                v-if="this.showTimes.length === this.form.length && this.formIsModified"
                :error-type="ERROR_TYPE_WARNING"
                :show-as-list="false"
                :display-errors-title="false"
                class="mb-5"
            >
                <i18n path="pages.settings.edit_dialog.modified_warning" tag="span"/>
            </alert-message>

            <alert-message
                v-if="preSubmitErrors && preSubmitErrors.length > 0"
                :error-type="ERROR_TYPE_ERROR"
                :messages="preSubmitErrors"
                :show-as-list="true"
                :display-errors-title="false"
                class="mb-5"
            />

            <alert-message
                v-if="warnings && warnings.length > 0"
                :error-type="ERROR_TYPE_WARNING"
                :messages="warnings"
                :show-as-list="true"
                @close="closeWarningsAlert"
                :display-errors-title="false"
                class="mb-5"
            />
        </modal>
    </div>
</template>

<script>
import Layout from '@/Shared/Layout';
import PageHeadline from "@/Shared/PageHeadline";
import Icon from "@/Shared/Icon";
import AuctionSettings from "./Components/AuctionSettings.vue";
import Modal from "@/Shared/Modals/Modal";
import {ErrorTypes} from "../../mixins/ErrorTypes";
import AlertMessage from "@/Shared/Alerts/AlertMessage";
import VueTimepicker from 'vue2-timepicker';
import moment from "moment";
import draggable from "vuedraggable";

export default {
    metaInfo: {title: 'Profile'},
    layout: Layout,
    components: {
        PageHeadline,
        Icon,
        AuctionSettings,
        Modal,
        AlertMessage,
        VueTimepicker,
        draggable
    },
    mixins: [
        ErrorTypes
    ],
    props: {
        timeZones: Object,
        auctionTimeZone: String,
        auctionStartTime: String,
        auctionEndTime: String,
        showTimes: Array,
        showTimesToday: Array,
        timeBetweenPriceDropsAndShows: {
            type: Number,
            default: 120
        },
        errorsFromServer: Object
    },
    data() {
        return {
            selectedTimezone: this.auctionTimeZone,
            preSubmitErrors: [],
            form: [],
            formIsModified: false,
            hourRanges: [],
            minuteRanges: [],
            errors: null,
            warnings: [],
            settingsSaved: false
        }
    },
    methods: {
        auctionTimeZoneDescription(timeZone) {
            return timeZone === this.auctionTimeZone ? this.$t('pages.settings.auction_timings.timezone.auction_time') : '';
        },
        editDefaultSettings() {
            this.warnings = [];
            this.initForm();
            this.settingsSaved = false;
            this.$refs.editDefaultSettingsDialog.show();
            this.initRanges();
        },
        saveSettings() {
            this.validateForm();
            if (this.preSubmitErrors.length > 0) {
                return;
            }

            this.warnings = [];
            let self = this;
            this.$refs.editDefaultSettingsDialog.disableConfirmButton();

            axios.post(route('settings.update'), { data: this.form } )
            .then(response => {
                self.$refs.editDefaultSettingsDialog.enableConfirmButton();
                if (Array.isArray(response.data.warnings) && response.data.warnings.length > 0 ) {
                    this.warnings = response.data.warnings;
                } else {
                    this.settingsSaved = true;
                }
                self.$inertia.reload({
                    preserveScroll: true,
                    preserveState: true,
                    only: ['showTimes'],
                    onSuccess: page => {
                        self.formIsModified = self.isFormModified();
                    }
                });
            })
            .catch(err => {
                self.errors = err.response.data.errors;
                self.$refs.editDefaultSettingsDialog.enableConfirmButton();

                if (err.response.status === 422) {// Bad Request
                    self.preSubmitErrors = [];
                    Object.values(err.response.data.errors).map((er) => {
                        er.map((v) => {
                            this.preSubmitErrors.push(v.message);
                        });
                    });
                } else // any other error
                {
                    console.log('Save settings other error occurred: ', err);
                }
            });
        },
        cancelSettingsEditing() {
            this.$refs.editDefaultSettingsDialog.hide();
            this.settingsSaved = false;
            this.initForm();
        },
        deleteItem(index) {
            let oldClass = this.$refs.settingsRows.$el.children[index].className;
            this.$refs.settingsRows.$el.children[index].className = 'transition-opacity duration-700 ease-in opacity-0';
            let that = this;
            setTimeout(() => {
                that.form.splice(index, 1);
                that.initRanges();
                that.formIsModified = that.isFormModified();
                that.$refs.settingsRows.$el.children[index].className = oldClass;
                this.updateTimeRanges(this.form.length-1);
            }, 710);
        },
        updateTimeRanges(index) {
            [index - 1, index, index + 1].filter((i) => i >= 0 && i < this.form.length).forEach((i) => {
                this.updatePriceDropTime(i);
                this.updateShowStart(i);
                this.updateShowEnd(i);
            });
            this.formIsModified = this.isFormModified();
        },
        updatePriceDropTime(index) {
            this.hourRanges[index].priceDropTime = this.hourRangePriceDrop(index);
            this.minuteRanges[index].priceDropTime = this.minuteRangePriceDrop(index, 'priceDropTime');
        },
        updateShowStart(index) {
            this.hourRanges[index].showStartTime = this.hourRangeShow(index, 'showStartTime');
            this.minuteRanges[index].showStartTime = this.minuteRangeShow(index, 'showStartTime');
        },
        updateShowEnd(index) {
            this.hourRanges[index].showEndTime = this.hourRangeShow(index, 'showEndTime');
            this.minuteRanges[index].showEndTime = this.minuteRangeShow(index, 'showEndTime');
        },
        toTimeString(value) {
            return this.$options.filters.formatTime(value, this.$t('locale.formatTimeTwelveHours'), this.timeZones[this.auctionTimeZone] )
        },
        toTimeArray(value, offsetInSeconds = 0) {
            let time = moment(value, this.$t('locale.formatTimeTwelveHours')).add(offsetInSeconds, 's');
            let retVal = time.format("HH:mm").split(':').map(v => parseInt(v));
            retVal.push(time);
            return retVal;
        },
        initForm() {
            this.form = this.showTimes.map((s) => {
                return {
                    show: s.show,
                    priceDropTime: this.toTimeString(s.price_drop),
                    showStartTime: this.toTimeString(s.start),
                    showEndTime: this.toTimeString(s.end)
                };
            });
            this.initRanges();
        },
        initRanges() {
            this.hourRanges = this.form.map( x => {
                return {
                    priceDropTime:  Array.from({ length: 23 }, (v, i) => i),
                    showStartTime:  Array.from({ length: 23 }, (v, i) => i),
                    showEndTime:  Array.from({ length: 23 }, (v, i) => i),
                };
            });
            this.minuteRanges = this.form.map( x => {
                return {
                    priceDropTime:  Array.from({ length: 60 }, (v, i) => i),
                    showStartTime:  Array.from({ length: 60 }, (v, i) => i),
                    showEndTime:  Array.from({ length: 60 }, (v, i) => i),
                };
            });
        },
        hourRangePriceDrop(index) {
            let min = this.minPriceDropTime(index, 'priceDropTime');
            let max = this.maxPriceDropTime(index, 'priceDropTime');
            // if show take into account the show start for min and end for max
            if (this.form[index].show) {
                let showStart = this.toTimeArray(this.form[index].showStartTime, this.timeBetweenPriceDropsAndShows);
                let showEnd = this.toTimeArray(this.form[index].showEndTime, this.timeBetweenPriceDropsAndShows * -1);
                if ( min.length > 2 && min[2].isBefore(showStart[2]) || index === 0 ) {
                    min = showStart;
                }
                if (max.length > 2 && max[2].isAfter(showEnd[2]) || index === this.form.length-1 ) {
                    max = showEnd;
                }
            }
            return Array.from({ length: 1 + max[0] - min[0] }, (v, i) => i + min[0]);
        },
        minuteRangePriceDrop(index, timeType) {
            let min = this.minPriceDropTime(index, timeType);
            let max = this.maxPriceDropTime(index, timeType);
            let val = this.toTimeArray(this.form[index][timeType]);

            // if show take into account the show start for min and end for max
            if (this.form[index].show) {
                let showStart = this.toTimeArray(this.form[index].showStartTime, this.timeBetweenPriceDropsAndShows);
                let showEnd = this.toTimeArray(this.form[index].showEndTime, this.timeBetweenPriceDropsAndShows * -1);
                if (min[2].isBefore(showStart[2]) || index === 0 ) {
                    min = showStart;
                }
                if (max[2].isAfter(showEnd[2]) || index === this.form.length-1 ) {
                    max = showEnd;
                }
            } else {
                let minPd = this.minPriceDropTime(index, 'priceDropTime');
                let minShow = this.minShow(index, 'showEndTime', this.timeBetweenPriceDropsAndShows);
                min = minPd[2].isAfter(minShow[2]) ? minPd : minShow;

                let maxPd = this.maxPriceDropTime(index, 'priceDropTime');
                let maxShow = this.maxShow(index, 'showStartTime', this.timeBetweenPriceDropsAndShows * -1);
                max = maxPd[2].isBefore(maxShow[2]) ? maxPd : maxShow;
            }
            return this.getValidRanges(val, max, min);
        },
        hourRangeShow(index, timeType) {
            let min, max;
            if (timeType === 'showStartTime') {
                min = this.minShow(index, 'showEndTime', this.timeInBetween);
                max = this.maxShow(index-1, 'showEndTime', this.timeInBetween * -1);
            }
            if (timeType === 'showEndTime') {
                min = this.minShow(index, 'showStartTime', this.timeInBetween);
                max = this.maxShow(index, 'showStartTime', this.timeInBetween * -1);
            }
            return Array.from({ length: 1 + max[0] - min[0] }, (v, i) => i + min[0]);
        },
        getValidRanges(val, max, min) {
            if (val[0] > max[0] || val[0] < min[0] || min[0] > max[0]) {
                return []; // previous and next overlap already
            }
            if (val[0] === min[0] && val[0] === max[0]) { // both previous and next hour are the same limit in wetween
                return Array.from({length: 1 + max[1] - min[1]}, (v, i) => i + min[1]);
            } else if (val[0] === min[0]) { // same hour as previous limit above selection in previous item
                return Array.from({length: 60 - min[1]}, (v, i) => i + min[1]);
            } else if (val[0] === max[0]) { // same hour as next limit below selection in next item
                return Array.from({length: 1+ max[1]}, (v, i) => i);
            } else { // no limitations full hours minutes
                return Array.from({length: 60}, (v, i) => i);
            }
        },
        minuteRangeShow(index, timeType) {
            let min, max;
            let val = this.toTimeArray(this.form[index][timeType]);

            if (timeType === 'showStartTime') {
                min = this.minShow(index, 'showEndTime', this.timeInBetween);
                max = this.maxShow(index-1, 'showEndTime', this.timeInBetween * -1);
            }
            if (timeType === 'showEndTime') {
                min = this.minShow(index, 'showStartTime', this.timeInBetween);
                max = this.maxShow(index, 'showStartTime', this.timeInBetween * -1);
            }
            return this.getValidRanges(val, max, min);
        },

        minPriceDropTime(index, timeType) {
            return index === 0 ? [0,0, moment('00:00', this.$t('locale.formatTimeTwelveHours'))] : this.toTimeArray(this.form[index - 1][timeType], this.timeBetweenPriceDropsAndShows);
        },
        maxPriceDropTime(index, timeType) {
            return (index >= this.form.length - 1) ? [23,59, moment('23:59', this.$t('locale.formatTimeTwelveHours'))] : this.toTimeArray(this.form[index + 1][timeType], (-1 * this.timeBetweenPriceDropsAndShows));
        },
        minShow(index, timeType, offset = 0) {
            if (index <= 0) {
                return [0,0, moment('00:00', this.$t('locale.formatTimeTwelveHours'))];
            }
            if (!this.form[index - 1].show) {
                return this.minShow(index - 1, timeType, offset);
            }
            return this.toTimeArray(this.form[index - 1][timeType], offset);
        },
        maxShow(index, timeType, offset = 0) {
            if  (index >= this.form.length - 1) {
                return [23,59, moment('23:59', this.$t('locale.formatTimeTwelveHours'))];
            }
            if (!this.form[index + 1].show) {
                return this.maxShow(index + 1, timeType, offset);
            }
            return this.toTimeArray(this.form[index + 1][timeType], offset);
        },
        isItemValid(item, index) {
            let priceDropTime =  moment(item.priceDropTime, this.$t('locale.formatTimeTwelveHours'));
            if (item.show) { // if show selected, check if price drop time is higher than the previous price drop but lower than the next price drop
                let showStart = moment(item.showStartTime, this.$t('locale.formatTimeTwelveHours')).add(this.timeBetweenPriceDropsAndShows, 's');
                let showEnd = moment(item.showEndTime, this.$t('locale.formatTimeTwelveHours')).add(-this.timeBetweenPriceDropsAndShows, 's');
                return {
                    res: priceDropTime.isBetween(showStart, showEnd, undefined, '[]'),
                    row: index + 1,
                    message: this.$t('pages.settings.edit_dialog.validation.price_drop_time.outside_of_show', {row: index + 1})
                };
            } else { // if show NOT selected, check if price drop is inside the show
                // In addition, check if live show start is after last live show end
                // and if live show end is before next live show start
                let min = this.minPriceDropTime(index, 'priceDropTime')[2];
                let minShow = this.minShow(index, 'showEndTime')[2].add(this.timeBetweenPriceDropsAndShows, 's');
                let lowest = moment.max(min, minShow);
                
                let max = this.maxPriceDropTime(index, 'priceDropTime')[2];
                let maxShow = this.maxShow(index, 'showStartTime')[2].add(-1 * this.timeBetweenPriceDropsAndShows, 's');
                let highest = moment.min(max, maxShow);
                return {
                    res: priceDropTime.isBetween(lowest, highest, undefined, '[]'),
                    row: index + 1,
                    message: this.$t('pages.settings.edit_dialog.validation.price_drop_time.conflicts', {row: index + 1})
                };
            }
        },
        validateForm() {
            // collect error rows
            this.preSubmitErrors = [];
            let that = this;
            this.form.map((item, index) => {
               let res = that.isItemValid(item, index);
               if (res.res === false) {
                   that.preSubmitErrors.push(res.message);
               }
            });
        },
        addAtEnd() {
            let item = {
                show: 0,
                priceDropTime: '11:54 PM',
                showStartTime: '11:52 PM',
                showEndTime: '11:56 PM'
            }
            this.form.splice(this.form.length, 0, item);
            this.initRanges();
            this.updateTimeRanges(this.form.length-2);
            this.updateTimeRanges(this.form.length-1);

        },
        isFormModified() { // modified means the number of entries is the same, but the data in the vorm has been changed compared to the original data from the server
            let modified = false;
            if(this.showTimes.length === this.form.length) {
                this.showTimes.map((s, index) => {
                    if (
                        s.show !== (this.form[index].show ? 1 : 0) ||
                        this.toTimeString(s.price_drop) !== this.form[index].priceDropTime ||
                        this.toTimeString(s.start) !== this.form[index].showStartTime ||
                        this.toTimeString(s.end) !== this.form[index].showEndTime
                    ) {
                        modified = true;
                    }
                });
            }
            return modified;
        },
        closeWarningsAlert() {
            this.warnings = [];
        }
    },
    computed: {
        selectedTimeZoneName: function () {
            return this.timeZones[this.selectedTimezone];
        },
        showTimesTodayDifferFromDefault: function() {
            return JSON.stringify(this.showTimesToday) !== JSON.stringify(this.showTimes);
        }
    },
    mounted() {
        this.initForm();
    }
}
</script>
